/**
 * DOM utility functions for common operations
 */

/**
 * Creates an element with given attributes and children
 * @param {string} tag - The HTML tag name
 * @param {Object} attributes - The element attributes
 * @param {Array|string|Node} children - The child elements or text content
 * @returns {HTMLElement} The created element
 */
export function createElement(tag, attributes = {}, children = []) {
    const element = document.createElement(tag);
    
    Object.entries(attributes).forEach(([key, value]) => {
        if (key === 'className') {
            element.className = value;
        } else if (key === 'dataset') {
            Object.entries(value).forEach(([dataKey, dataValue]) => {
                element.dataset[dataKey] = dataValue;
            });
        } else if (key.startsWith('on') && typeof value === 'function') {
            element.addEventListener(key.slice(2).toLowerCase(), value);
        } else {
            element.setAttribute(key, value);
        }
    });
    
    if (Array.isArray(children)) {
        element.append(...children);
    } else if (children instanceof Node || typeof children === 'string') {
        element.append(children);
    }
    
    return element;
}

/**
 * Creates a button element with given text and click handler
 * @param {string} text - The button text
 * @param {Function} onClick - The click event handler
 * @param {string} className - Additional CSS classes
 * @returns {HTMLButtonElement} The created button
 */
export function createButton(text, onClick, className = '') {
    return createElement('button', {
        className,
        onClick
    }, text);
}

/**
 * Shows an element with a fade-in animation
 * @param {HTMLElement} element - The element to show
 * @param {number} duration - Animation duration in milliseconds
 */
export function fadeIn(element, duration = 200) {
    element.style.opacity = '0';
    element.style.display = 'block';
    
    requestAnimationFrame(() => {
        element.style.transition = `opacity ${duration}ms ease`;
        element.style.opacity = '1';
    });
}

/**
 * Hides an element with a fade-out animation
 * @param {HTMLElement} element - The element to hide
 * @param {number} duration - Animation duration in milliseconds
 * @returns {Promise} Resolves when the animation is complete
 */
export function fadeOut(element, duration = 200) {
    return new Promise(resolve => {
        element.style.transition = `opacity ${duration}ms ease`;
        element.style.opacity = '0';
        
        setTimeout(() => {
            element.style.display = 'none';
            resolve();
        }, duration);
    });
}

/**
 * Updates progress bar and text
 * @param {HTMLElement} progressBar - The progress bar element
 * @param {HTMLElement} progressText - The progress text element
 * @param {number} progress - Progress value (0-100)
 * @param {string} text - Progress text
 */
export function updateProgress(progressBar, progressText, progress, text) {
    if (progressBar) {
        progressBar.style.width = `${progress}%`;
    }
    if (progressText) {
        progressText.textContent = text || `${Math.round(progress)}%`;
    }
}

/**
 * Adds drag and drop functionality to an element
 * @param {HTMLElement} element - The drop zone element
 * @param {Function} onDrop - Function to handle dropped files
 */
export function setupDropZone(element, onDrop) {
    const preventDefault = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const highlight = () => element.classList.add('drag-over');
    const unhighlight = () => element.classList.remove('drag-over');

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        element.addEventListener(eventName, preventDefault, false);
    });

    ['dragenter', 'dragover'].forEach(eventName => {
        element.addEventListener(eventName, highlight, false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
        element.addEventListener(eventName, unhighlight, false);
    });

    element.addEventListener('drop', (e) => {
        const dt = e.dataTransfer;
        const files = dt.files;
        onDrop(files);
    }, false);
} 